import type { z } from "zod";
import type {
    billLineItemResponseSchema,
    billResponseSchema,
} from "../schemas/domains/bills/bill-response.schema";
import { AccountOverrideEntry, ClassAssignment } from "./transaction";
import { Counterparty } from "./counterparty";
import { FinancialAccountBase } from "./financialAccount";

export enum BillStatus {
    DRAFT = "draft",
    OPEN = "open",
    PAID = "paid",
    VOID = "void",
}

export enum BillSource {
    RAMP = "ramp",
    IMPORTER = "importer",
    BILLCOM = "billcom",
    MCP = "mcp",
    QUICKBOOKS = "quickbooks",
}

export enum BillLineItemKind {
    ITEM = "item",
    TAX = "tax",
    DISCOUNT = "discount",
    DEPOSIT = "deposit",
}

/**
 * Base type for Bill - allows base types on relations.
 * Used by entity classes (TypeORM) and contracts.
 */
export interface BillBase {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    due: Date | null;
    paidAt: Date | null;
    dateOnly: string | null;
    paidDateOnly: string | null;
    dueDateOnly: string | null;
    total: number;
    subtotal: number;
    tax: number;
    discount: number;
    deposit: number | null;
    currency: string;
    counterparty?: Counterparty | null;
    description: string | null;
    memo: string | null;
    status: BillStatus;
    externalStatus: BillStatus | null;
    source: BillSource;
    sourceId: string | null;
    lines?: BillLineItemBase[];
    entityId: number;
    financialAccountId: number | null;
    financialAccount?: FinancialAccountBase | null;
    invoiceNumber: string | null;
    financialDocumentIds?: number[];
}

/**
 * Strict Bill DTO shape, derived from the API response schema.
 */
export type Bill = z.infer<typeof billResponseSchema>;

export interface BillLineItemAccountOverrides {
    accountOverrides?: AccountOverrideEntry[];
    accountsPayableOverrides?: AccountOverrideEntry[];
}

/**
 * Base type for BillLineItem - allows base types on relations.
 */
export interface BillLineItemBase extends BillLineItemAccountOverrides {
    id: string;
    amount: number;
    matchedAmount: number;
    unitAmount?: number | null;
    currency: string;
    description: string | null;
    quantityDecimal: number | null;
    sourceId: string;
    accountId?: string | null;
    accountsPayableId?: string | null;
    classAssignments?: ClassAssignment[];
    billId: string;
    bill?: BillBase;
    kind: BillLineItemKind;
}

/**
 * Strict BillLineItem DTO shape, derived from the API response schema.
 * The parent back-reference is attached client-side, not sent on the wire.
 */
export type BillLineItem = z.infer<typeof billLineItemResponseSchema> & {
    bill?: Bill;
};

export interface BillSort {
    field: string;
    order: "ASC" | "DESC";
}

export enum BillSortValue {
    DATE_ASC = "DATE_ASC",
    DATE_DESC = "DATE_DESC",
    DUE_DATE_ASC = "DUE_DATE_ASC",
    DUE_DATE_DESC = "DUE_DATE_DESC",
    STATUS_ASC = "STATUS_ASC",
    STATUS_DESC = "STATUS_DESC",
    EXTERNAL_STATUS_ASC = "EXTERNAL_STATUS_ASC",
    EXTERNAL_STATUS_DESC = "EXTERNAL_STATUS_DESC",
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    ACCOUNT_ASC = "ACCOUNT_ASC",
    ACCOUNT_DESC = "ACCOUNT_DESC",
    AMOUNT_ASC = "AMOUNT_ASC",
    AMOUNT_DESC = "AMOUNT_DESC",
    NONE = "NONE",
}

export enum BillByCounterpartySortValue {
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    PAST_DUE_1_30_ASC = "PAST_DUE_1_30_ASC",
    PAST_DUE_1_30_DESC = "PAST_DUE_1_30_DESC",
    PAST_DUE_31_60_ASC = "PAST_DUE_31_60_ASC",
    PAST_DUE_31_60_DESC = "PAST_DUE_31_60_DESC",
    PAST_DUE_61_PLUS_ASC = "PAST_DUE_61_PLUS_ASC",
    PAST_DUE_61_PLUS_DESC = "PAST_DUE_61_PLUS_DESC",
    OVERDUE_ASC = "OVER_DUE_ASC",
    OVERDUE_DESC = "OVER_DUE_DESC",
    OPEN_ASC = "OPEN_ASC",
    OPEN_DESC = "OPEN_DESC",
    PAID_ASC = "PAID_ASC",
    PAID_DESC = "PAID_DESC",
    BILLS_COUNT_ASC = "BILLS_COUNT_ASC",
    BILLS_COUNT_DESC = "BILLS_COUNT_DESC",
}

export enum BillDueDateSubsegment {
    PAST_DUE_1_TO_30 = "past_due_1_to_30",
    PAST_DUE_31_TO_60 = "past_due_31_to_60",
    PAST_DUE_61_PLUS = "past_due_61_plus",
    OVERDUE = "overdue",
}

export const BillDueDateLabels = {
    [BillDueDateSubsegment.OVERDUE]: "Overdue",
    [BillDueDateSubsegment.PAST_DUE_1_TO_30]: "1-30d",
    [BillDueDateSubsegment.PAST_DUE_31_TO_60]: "31-60d",
    [BillDueDateSubsegment.PAST_DUE_61_PLUS]: "61d+",
};

export enum BillViewOptionGroup {
    BILL = "bill",
    COUNTERPARTY = "counterparty",
    LINE_ITEMS = "line_items",
}

export interface BillViewOptions {
    groupBy: BillViewOptionGroup;
}

export interface BillStatEntity {
    id: number;
    count: number;
    total: number;
}

export interface BillStatSubsegment {
    label: BillDueDateSubsegment;
    count: number;
    total: number;
    entities?: BillStatEntity[];
}

export interface BillStat {
    count: number;
    total: number;
    subsegments?: BillStatSubsegment[];
}

export interface BillStats {
    draft: BillStat;
    open: BillStat;
    paid: BillStat;
    void: BillStat;
}

export interface BillsByCounterpartyItem {
    counterpartyId: string;
    counterparty?: Counterparty;
    open: number;
    paid: number;
    pastDue1To30: number;
    pastDue31To60: number;
    pastDue61Plus: number;
    overdue: number;
    billCount: number;
    totalCounterparties: number;
    entityIds: number[];
}
